import {
  Box,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { useContext, useCallback, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import { LayoutContext } from "../../../contexts/layout/context";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { Run } from "../../../services/perspective_api/__generated__/perspective_server-types";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { useSupplyWalkEdits } from "../../../contexts/supply_walk_edits/provider";
import { useModal } from "../../../contexts/modal/hooks";
import { useForceUserEditConclude } from "../../modals/force_user_edit_conclude";
import dayjs from "../../../utils/dayjs";

export const RunSelectorComponent = (props: {
  loading: boolean;
  mainRun: Run | null;
  setMainRun: (run: Run | null) => void;
  comparedRun: Run | null;
  setComparedRun: (run: Run | null) => void;
  runs: Run[];
}) => {
  const { editedRun } = useContext(VersionedDataContext);
  const { isComparing, setIsComparing } = useContext(LayoutContext);
  const { editMode, discardChanges } = useContext(DemandWalkEditsContext);
  const { openForceUserEditModal } = useForceUserEditConclude();
  const supplyWalkEdits = useSupplyWalkEdits();
  const { openModal, closeModal } = useModal();
  const { loading, mainRun, setMainRun, comparedRun, setComparedRun, runs } =
    props;

  // Store the pending run selection
  const pendingRunRef = useRef<Run | null>(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    editMode ||
    (supplyWalkEdits?.hasUnapprovedEdits &&
      supplyWalkEdits.hasUnapprovedEdits()) ||
    false;

  // Handle run selection with navigation guards
  const handleRunSelection = useCallback(
    (newRun: Run | null) => {
      // Check for unsaved changes dynamically at the time of navigation
      const currentHasUnsavedChanges =
        editMode ||
        (supplyWalkEdits?.hasUnapprovedEdits &&
          supplyWalkEdits.hasUnapprovedEdits()) ||
        false;

      if (currentHasUnsavedChanges) {
        // Store the pending run selection
        pendingRunRef.current = newRun;

        // Use the same modal as the navigation guard
        openModal({
          disableStepViewer: true,
          maxWidth: "sm",
          steps: [
            {
              id: "supply_unsaved_changes",
              title: "Unsaved Changes",
              content:
                "Please approve your changes before navigating away or they will be lost.",
              customActions: (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      // Discard changes from both contexts
                      discardChanges();
                      if (supplyWalkEdits?.discardChanges) {
                        supplyWalkEdits.discardChanges();
                      }
                      closeModal();
                      // Now proceed with the run selection
                      setMainRun(pendingRunRef.current);
                      pendingRunRef.current = null;
                    }}
                  >
                    DISCARD CHANGES
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      closeModal();
                    }}
                  >
                    REVIEW CHANGES
                  </Button>
                </Box>
              ),
            },
          ],
        });
      } else {
        // No unsaved changes, proceed directly
        setMainRun(newRun);
      }
    },
    [
      editMode,
      discardChanges,
      supplyWalkEdits,
      openModal,
      closeModal,
      setMainRun,
    ]
  );

  const mainOrLastRun = mainRun || (runs.length > 0 ? runs[0] : null);

  const compareToRuns = runs;

  if (mainOrLastRun === null) {
    return null;
  }

  const previewRun = (
    <Box>
      <Box sx={{ fontSize: `12px !important` }}>preview run (1st)</Box>
      <Select value="edits" variant="standard" size="small" disabled={true}>
        <MenuItem onClick={() => {}} value="edits">
          current edits
        </MenuItem>
      </Select>
    </Box>
  );

  const mainRunSelector = (
    <Box>
      <Box sx={{ fontSize: `12px !important` }}>
        {editedRun ? "edited run" : "current run"}{" "}
        {isComparing ? (editedRun ? "(2nd)" : "(1st)") : ""}
      </Box>
      <Select
        variant="standard"
        size="small"
        value={mainOrLastRun}
        disabled={loading || runs.length === 0 || Boolean(editedRun)}
        renderValue={(value) => {
          if (value === null) {
            // it's never run if we already have runs
            return "Select a run";
          }
          return `${value.displayId} ${value.name || ""} (${dayjs.utc(value.createdAt).local().format("MM/DD/YYYY HH:mm")})`;
        }}
      >
        {runs.map((run, i) => (
          <MenuItem
            key={i}
            value={i}
            onClick={() => {
              // if it's the topmost item then select null instead
              handleRunSelection(i === 0 ? null : runs[i]);
            }}
          >
            {/* @ts-ignore */}
            {run?.displayId ? `${run.displayId}` : ""} &nbsp;
            {run?.name || ""} &nbsp; (
            {dayjs.utc(run?.createdAt).local().format("MM/DD/YYYY HH:mm")})
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const comparedRunSelector = (
    <Box>
      <Box sx={{ fontSize: `12px !important` }}>compared to (2nd)</Box>
      <Select
        variant="standard"
        size="small"
        value={comparedRun || mainOrLastRun}
        disabled={loading || runs.length === 0}
        renderValue={(value) => {
          if (value === null) {
            // it's never run if we already have runs
            return "Select a run";
          }
          if (value === mainOrLastRun && !editedRun) {
            return "Select a run";
          }
          return `${value.displayId} ${value.name || ""} (${dayjs.utc(value.createdAt).local().format("MM/DD/YYYY HH:mm")})`;
        }}
      >
        {compareToRuns.map((run, i) => (
          <MenuItem
            key={i}
            value={i}
            onClick={() => {
              setComparedRun(compareToRuns[i]);
            }}
          >
            {/* @ts-ignore */}
            {run?.displayId ? `${run.displayId}` : ""} &nbsp;
            {run?.name || ""} &nbsp; (
            {dayjs.utc(run?.createdAt).local().format("MM/DD/YYYY HH:mm")})
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {editedRun ? previewRun : mainRunSelector}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 1,
          width: "30px",
        }}
      >
        <Tooltip title={isComparing ? "Comparing to" : `Make a comparison`}>
          <IconButton
            sx={{ mt: 1 }}
            onClick={() => {
              if (editMode) {
                openForceUserEditModal();
                return;
              }
              setIsComparing(!isComparing);
            }}
            disabled={loading || runs.length === 0 || isComparing}
          >
            <CompareArrowsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isComparing && (
        <>
          {editedRun ? mainRunSelector : comparedRunSelector}
          {!editedRun && (
            <Tooltip title="Clear comparison">
              <IconButton
                sx={{ mt: 2 }}
                onClick={() => {
                  if (editMode) {
                    openForceUserEditModal();
                    return;
                  }
                  setIsComparing(false);
                  setComparedRun(null);
                }}
                disabled={loading || runs.length === 0}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};
